/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { cva } from "class-variance-authority"
import type { ComponentProps, FunctionComponent } from "react"

import { cn } from "@/utils/cn"

const iconVariants = cva(
    "col-start-1 row-start-1 transition-[filter,opacity,scale] duration-250 ease-in-out motion-reduce:transition-none",
    {
        variants: {
            visible: {
                true: "",
                false: "blur-[2px] opacity-0 scale-25"
            }
        }
    }
)

type Icon = FunctionComponent<ComponentProps<"svg">>

/** Two or more icons; swapping a single icon has no purpose. */
type Icons = readonly [Icon, Icon, ...Icon[]]

/**
 * Tuple indexes from `0` through the last icon.
 *
 * `Partial<T>["length"]` produces all lengths from `0` to `T["length"]`;
 * excluding the full length leaves the valid zero-based indexes.
 */
type Index<T extends readonly unknown[]> = Exclude<Partial<T>["length"], T["length"]>

interface IconSwapProps<T extends Icons> {
    /**
     * The icons to swap between. A literal tuple keeps `active` type-safe.
     */
    icons: T
    /**
     * Zero-based position of the visible icon.
     */
    active: Index<T>
    className?: string
    /**
     * Classes applied to every icon. A `size-*` class here overrides the button's
     * icon size; the primitives only size icons that have no `size-*` class.
     */
    iconClassName?: string
}

/**
 * Crossfades between multiple icons in the same position.
 *
 * All icons remain mounted so a change can animate directly from the current
 * state. Rapid changes also reverse the transition from its current position.
 * Icons are hidden from assistive technology; the surrounding control is
 * responsible for exposing the state.
 */
export const IconSwap = <const T extends Icons>({ icons, active, className, iconClassName }: IconSwapProps<T>) => (
    <span className={cn("inline-grid", className)}>
        {icons.map((Icon, index) => (
            <Icon
                // biome-ignore lint/suspicious/noArrayIndexKey: a position in the tuple is the icon's identity
                key={index}
                aria-hidden="true"
                className={cn(iconVariants({ visible: index === active }), iconClassName)}
            />
        ))}
    </span>
)
